import { FIELD_ID_RE } from '../../shared/fieldIds';
import {
  countAnswerFields,
  FIELD_TYPES,
  isLayoutType,
  LIMITS,
} from '../../shared/fieldTypes';
import { PREFILL_SOURCES, prefillFit } from '../../shared/prefillSources';
import { recognizeVideoUrl } from '../../shared/video';

// Client-side mirror of the server's save checks (build plan §2.3, §3.7
// step 2). Rules and messages match pitpay-graphql
// shared/form-builder/definition.js and resolvers/form-builder/admin/
// shared/validate.js — change both together.
//
// validateForSave(snapshot, { live, lockedFieldIds }) → { [key]: message } with keys
// `name`, `description`, `notify_recipients`, `definition` and
// `definition.<field id>` (first problem per key), the same keys
// fieldErrorsOf gives for a server 400.

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_MAX = 254;

const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const isMissing = (v) => v === undefined || v === null;
const isBlank = (v) => typeof v !== 'string' || v.trim() === '';
const isAssetId = (v) => Number.isInteger(v) && v > 0;
const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
// Postgres can't store U+0000 in jsonb, so text holding one is refused.
const hasNul = (v) => typeof v === 'string' && v.indexOf('\u0000') !== -1;
const badCharacter = (noun) => `The ${noun} has a character that isn't allowed`;

// Each check returns an error message or null.
const bool = (message) => (v) => typeof v === 'boolean' ? null : message;
const text = (max, message, noun) => (v) => {
  if (hasNul(v)) return badCharacter(noun);
  return typeof v === 'string' && v.length <= max ? null : message;
};
const integer = (min, max, message) => (v) =>
  Number.isInteger(v) && v >= min && v <= max ? null : message;
const oneOf = (choices, message) => (v) => choices.includes(v) ? null : message;
const assetId = (v) => (isAssetId(v) ? null : 'Upload the image again');

// `first` ({ max, message }) gives the first item its own length limit.
const textList = (min, max, plural, singular, first) => (v) => {
  if (!Array.isArray(v) || !v.every((item) => typeof item === 'string')) {
    return `The ${plural} must be a list of text`;
  }
  if (v.length < min || v.length > max) {
    return min > 0
      ? `Use ${min} to ${max} ${plural}`
      : `Use up to ${max} ${plural}`;
  }
  if (first && v.length && v[0].length > first.max) return first.message;
  const rest = first ? v.slice(1) : v;
  if (rest.some((item) => item.length > LIMITS.optionText)) {
    return `Keep each ${singular} under ${LIMITS.optionText} characters`;
  }
  if (v.some(hasNul))
    return `The ${plural} have a character that isn't allowed`;
  return null;
};

const videoUrl = (v) =>
  typeof v === 'string' &&
  v.length <= LIMITS.videoUrl &&
  !hasNul(v) &&
  (v.trim() === '' || !!recognizeVideoUrl(v))
    ? null
    : 'Add a YouTube or Vimeo link';

const options = textList(0, LIMITS.options, 'options', 'option');

// A single (agreement) checkbox keeps its agreement copy in options[0], so
// that one may be long; the rest are kept for switching back to a list.
const agreementOptions = textList(0, LIMITS.options, 'options', 'option', {
  max: LIMITS.agreementText,
  message: `Keep the agreement text under ${LIMITS.agreementText} characters`,
});
const checkboxOptions = (v, config) =>
  config.single === true ? agreementOptions(v) : options(v);

const columns = textList(
  LIMITS.columnsMin,
  LIMITS.columnsMax,
  'columns',
  'column'
);

const ALLOW_OTHER_MESSAGE = 'Allow other must be on or off';

const CONFIG_CHECKS = {
  name: [['middle', bool('Middle name must be on or off')]],
  dropdown: [
    ['options', options],
    ['allow_other', bool(ALLOW_OTHER_MESSAGE)],
  ],
  radio: [
    ['options', options],
    ['allow_other', bool(ALLOW_OTHER_MESSAGE)],
  ],
  checkbox: [
    ['options', checkboxOptions],
    ['single', bool('Single checkbox must be on or off')],
    [
      'agree_label',
      text(
        LIMITS.agreeLabel,
        `Keep the checkbox text under ${LIMITS.agreeLabel} characters`,
        'checkbox text'
      ),
    ],
  ],
  table: [
    ['columns', columns],
    [
      'rows',
      integer(
        LIMITS.tableRowsMin,
        LIMITS.tableRowsMax,
        `Use ${LIMITS.tableRowsMin} to ${LIMITS.tableRowsMax} rows`
      ),
    ],
  ],
  repeater: [
    ['columns', columns],
    [
      'add_label',
      text(
        LIMITS.addLabel,
        `Keep the button label under ${LIMITS.addLabel} characters`,
        'button label'
      ),
    ],
    [
      'max',
      integer(
        LIMITS.repeaterMaxMin,
        LIMITS.repeaterMaxMax,
        `Allow ${LIMITS.repeaterMaxMin} to ${LIMITS.repeaterMaxMax} entries`
      ),
    ],
  ],
  matrix: [
    [
      'rows',
      textList(LIMITS.matrixRowsMin, LIMITS.matrixRowsMax, 'rows', 'row'),
    ],
    [
      'cols',
      textList(LIMITS.matrixColsMin, LIMITS.matrixColsMax, 'columns', 'column'),
    ],
  ],
  rating: [
    [
      'max',
      integer(
        LIMITS.ratingMaxMin,
        LIMITS.ratingMaxMax,
        `Use ${LIMITS.ratingMaxMin} to ${LIMITS.ratingMaxMax} stars`
      ),
    ],
  ],
  section: [
    ['banner', bool('Header image must be on or off')],
    ['banner_asset_id', assetId],
  ],
  description: [
    [
      'text',
      text(
        LIMITS.descriptionText,
        `Keep the text under ${LIMITS.descriptionText} characters`,
        'text'
      ),
    ],
  ],
  image: [
    [
      'size',
      oneOf(['small', 'medium', 'large'], 'Pick small, medium or large'),
    ],
    ['align', oneOf(['left', 'center', 'right'], 'Pick left, center or right')],
    ['asset_id', assetId],
  ],
  video: [
    ['url', videoUrl],
    [
      'caption',
      text(
        LIMITS.videoCaption,
        `Keep the caption under ${LIMITS.videoCaption} characters`,
        'caption'
      ),
    ],
  ],
};

const STRING_KEYS = [
  ['label', LIMITS.label, 'label'],
  ['help', LIMITS.help, 'help text'],
  ['placeholder', LIMITS.placeholder, 'placeholder'],
];

const BOOLEAN_KEYS = [
  ['required', 'Required must be on or off'],
  ['acct', 'Account info must be on or off'],
];

const firstError = (checks) => {
  for (let i = 0; i < checks.length; i += 1) {
    const message = checks[i]();
    if (message) return message;
  }
  return null;
};

const stringError = (field, layout) =>
  firstError(
    STRING_KEYS.filter(
      ([key]) => !(layout && key === 'placeholder') && !isMissing(field[key])
    ).map(([key, max, noun]) => () => {
      if (typeof field[key] !== 'string') return `The ${noun} must be text`;
      if (field[key].length > max) {
        return `Keep the ${noun} under ${max} characters`;
      }
      return hasNul(field[key]) ? badCharacter(noun) : null;
    })
  );

const mapError = (source, fieldType) => {
  const known =
    typeof source === 'string' && PREFILL_SOURCES.find((s) => s.key === source);
  if (!known) return 'Unknown pre-fill source';
  return prefillFit(source, fieldType)
    ? null
    : `Can't pre-fill this field from ${known.label}`;
};

const answerKeysError = (field) =>
  firstError([
    ...BOOLEAN_KEYS.filter(([key]) => !isMissing(field[key])).map(
      ([key, message]) =>
        () =>
          typeof field[key] === 'boolean' ? null : message
    ),
    () => (isMissing(field.map) ? null : mapError(field.map, field.type)),
  ]);

const configError = (field) => {
  if (!isMissing(field.config) && !isPlainObject(field.config)) {
    return "This field's settings are invalid";
  }
  const config = field.config || {};
  return firstError(
    (CONFIG_CHECKS[field.type] || [])
      .filter(([key]) => !isMissing(config[key]))
      .map(
        ([key, check]) =>
          () =>
            check(config[key], config)
      )
  );
};

// One field's first structural problem, or null (server normalizeField).
export const fieldStructureError = (field) => {
  if (!isPlainObject(field)) return 'This field is invalid';
  if (typeof field.id !== 'string' || !FIELD_ID_RE.test(field.id)) {
    return 'This field has an invalid id';
  }
  if (typeof field.type !== 'string' || !has(FIELD_TYPES, field.type)) {
    return 'Unknown field type';
  }
  const layout = isLayoutType(field.type);
  return firstError([
    () => stringError(field, layout),
    () => (layout ? null : answerKeysError(field)),
    () => configError(field),
  ]);
};

// → [{ field, message }] (server normalizeDefinition's errors).
export const structuralErrors = (fields) => {
  if (!Array.isArray(fields)) {
    return [{ field: 'definition', message: 'The form definition is invalid' }];
  }
  if (fields.length > LIMITS.fields) {
    return [
      {
        field: 'definition',
        message: `Forms can have up to ${LIMITS.fields} fields`,
      },
    ];
  }
  const errors = [];
  const seen = {};
  fields.forEach((field, index) => {
    const id = isPlainObject(field) ? field.id : undefined;
    if (typeof id !== 'string' || !FIELD_ID_RE.test(id)) {
      errors.push({
        field: 'definition',
        message: `Field ${index + 1} has an invalid id`,
      });
      return;
    }
    if (has(seen, id)) {
      errors.push({
        field: `definition.${id}`,
        message: 'Each field needs its own id',
      });
      return;
    }
    seen[id] = true;
    const message = fieldStructureError(field);
    if (message) errors.push({ field: `definition.${id}`, message });
  });
  return errors;
};

// Blank, missing or repeated (case-insensitive) entries.
const badList = (list) => {
  if (!Array.isArray(list) || !list.length) return true;
  const seen = {};
  return list.some((item) => {
    if (isBlank(item)) return true;
    const key = item.trim().toLowerCase();
    if (has(seen, key)) return true;
    seen[key] = true;
    return false;
  });
};

const OPTIONS_MESSAGE = "Options can't be blank or repeated";

const checkboxMessage = (config) => {
  if (!config.single) return badList(config.options) ? OPTIONS_MESSAGE : null;
  return Array.isArray(config.options) && !isBlank(config.options[0])
    ? null
    : 'Add the agreement text';
};

const INCOMPLETE = {
  dropdown: (c) => (badList(c.options) ? OPTIONS_MESSAGE : null),
  radio: (c) => (badList(c.options) ? OPTIONS_MESSAGE : null),
  checkbox: checkboxMessage,
  table: (c) =>
    badList(c.columns) ? "Columns can't be blank or repeated" : null,
  repeater: (c) =>
    badList(c.columns) ? "Columns can't be blank or repeated" : null,
  matrix: (c) =>
    badList(c.rows) || badList(c.cols)
      ? "Rows and columns can't be blank or repeated"
      : null,
  description: (c) => (isBlank(c.text) ? 'Add the description text' : null),
  image: (c) => (isAssetId(c.asset_id) ? null : 'Upload an image'),
  section: (c) =>
    c.banner && !isAssetId(c.banner_asset_id) ? 'Upload a header image' : null,
  video: (c) =>
    recognizeVideoUrl(c.url) ? null : 'Add a YouTube or Vimeo link',
};

export const incompleteMessage = (field) => {
  const config = isPlainObject(field.config) ? field.config : {};
  if (!isLayoutType(field.type) && isBlank(field.label)) return 'Add a label';
  const rule = INCOMPLETE[field.type];
  return rule ? rule(config) : null;
};

// → [{ field, message }]: the form-level error first, then one per
// incomplete field in form order (server completenessErrors).
export const completenessErrors = (fields) => {
  const list = Array.isArray(fields) ? fields.filter(isPlainObject) : [];
  const errors = [];
  if (!countAnswerFields(list)) {
    errors.push({
      field: 'definition',
      message: 'Add at least one question before going live',
    });
  }
  list.forEach((field) => {
    const message = incompleteMessage(field);
    if (message) errors.push({ field: `definition.${field.id}`, message });
  });
  return errors;
};

const recipientsError = (list) => {
  const items = Array.isArray(list) ? list : [];
  const seen = {};
  let count = 0;
  for (let i = 0; i < items.length; i += 1) {
    const email = String((items[i] && items[i].email) || '').trim();
    if (!EMAIL_RE.test(email) || email.length > EMAIL_MAX) {
      return `“${email}” isn't a valid email`;
    }
    const name = String((items[i] && items[i].name) || '').trim();
    if (name.length > LIMITS.formName) {
      return `Keep each name under ${LIMITS.formName} characters`;
    }
    if (!seen[email.toLowerCase()]) count += 1;
    seen[email.toLowerCase()] = true;
  }
  return count > LIMITS.recipients
    ? `Send to ${LIMITS.recipients} people or fewer`
    : null;
};

const nameError = (value) => {
  const name = typeof value === 'string' ? value.trim() : '';
  if (!name) return 'Give the form a name';
  return name.length > LIMITS.formName
    ? `Keep the name under ${LIMITS.formName} characters`
    : null;
};

// Form-level settings (plan §3.7 step 2) → [{ field, message }].
export const settingsErrors = (snapshot) => {
  const errors = [];
  const name = nameError(snapshot.name);
  if (name) errors.push({ field: 'name', message: name });
  const description = snapshot.description || '';
  if (description.length > LIMITS.formDescription) {
    errors.push({
      field: 'description',
      message: `Keep the description under ${LIMITS.formDescription} characters`,
    });
  }
  const recipients = recipientsError(snapshot.notify_recipients);
  if (recipients)
    errors.push({ field: 'notify_recipients', message: recipients });
  return errors;
};

export const toErrorMap = (list) => {
  const map = {};
  list.forEach(({ field, message }) => {
    if (!has(map, field)) map[field] = message;
  });
  return map;
};

// Settings first, then structure, then (when live) completeness — the
// order the server reports them in.
// `lockedFieldIds` (a W-9 form's fixed IRS fields) are left out of the
// completeness check, as the server leaves them out: the promoter can't
// edit them, and the template keeps some on purpose incomplete by the
// regular rules (an empty description under the Exemptions heading).
export const validateForSave = (
  snapshot,
  { live = false, lockedFieldIds = [] } = {}
) => {
  const errors = settingsErrors(snapshot).concat(
    structuralErrors(snapshot.fields)
  );
  if (!live) return toErrorMap(errors);
  const locked = Array.isArray(lockedFieldIds) ? lockedFieldIds : [];
  const own = (snapshot.fields || []).filter(
    (f) => !(f && locked.includes(f.id))
  );
  return toErrorMap(errors.concat(completenessErrors(own)));
};

// Human count for a toast: "1 field needs your attention".
export const attentionMessage = (errorMap) => {
  const n = Object.keys(errorMap).length;
  return `${n} ${n === 1 ? 'field needs' : 'fields need'} your attention`;
};

// The first `definition.<id>` key's field id, for selecting / scrolling.
export const firstFieldErrorId = (errorMap, fields) => {
  const hit = (fields || []).find((f) => has(errorMap, `definition.${f.id}`));
  return hit ? hit.id : null;
};
